import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  Download, 
  Calendar as CalendarIcon,
  ChevronDown,
  Printer,
  Phone,
  Mail,
  MoreVertical,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';
import { supabase } from '../supabaseClient';
import { useNotifications } from '../context/NotificationContext';
import { useNavigate } from 'react-router-dom';
import StatusDropdown from '../components/StatusDropdown';
import VerificationModal from '../components/VerificationModal';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import './Reports.css';

const Reports = () => {
  const navigate = useNavigate();
  const { addNotification } = useNotifications();
  
  const activeStaff = JSON.parse(localStorage.getItem('gdt_active_staff') || '{}');
  const staffName = activeStaff.full_name || '';
  const isAdmin = activeStaff?.type === 'Administrador' || activeStaff?.type === 'Secretaria/o';

  const [activeTab, setActiveTab] = useState('diario');
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedProf, setSelectedProf] = useState(isAdmin ? 'VER TODOS' : (staffName || 'VER TODOS'));
  const [professionals, setProfessionals] = useState([]);

  // Verification modal states
  const [isVerifyModalOpen, setIsVerifyModalOpen] = useState(false);
  const [selectedAppt, setSelectedAppt] = useState(null);

  const fetchConfig = async () => {
    try {
      const { data, error } = await supabase.from('professionals').select('*').order('full_name', { ascending: true });
      if (error) throw error;
      setProfessionals(data || []);
    } catch (err) {
      console.error("Error fetching professionals for Reports:", err);
    }
  };

  const reportButtons = [
    { id: 'diario', label: 'LISTADO DIARIO' },
    { id: 'otorgados', label: 'TURNOS OTORGADOS' },
    { id: 'cancelados', label: 'TURNOS CANCELADOS' },
    { id: 'borrados', label: 'TURNOS BORRADOS' },
    { id: 'pacientes-borrados', label: 'PACIENTES BORRADOS' },
    { id: 'caja-borrados', label: 'REG. CAJA BORRADOS' },
    { id: 'espera', label: 'SALA DE ESPERA' },
    { id: 'ausentes', label: 'PACIENTES AUSENTES' },
    { id: 'informes', label: 'INFORMES' },
    { id: 'informes-borrados', label: 'INFORMES BORRADOS' },
    { id: 'graficos', label: 'ANALÍTICAS (GRÁFICOS)' },
  ];

  const fetchReportData = async () => {
    setLoading(true);
    setError(null);
    try {
      if (['diario', 'otorgados', 'cancelados', 'ausentes', 'espera', 'informes', 'graficos'].includes(activeTab)) {
        let query = supabase.from('appointments').select('*');
        
        if (selectedDate) query = query.eq('appointment_date', selectedDate);
        
        if (selectedProf && selectedProf !== 'VER TODOS') {
          query = query.eq('professional_name', selectedProf);
        }

        if (activeTab === 'cancelados') {
          query = query.eq('status', 'cancelado');
        } else if (activeTab === 'ausentes') {
          query = query.eq('status', 'ausente');
        } else if (activeTab === 'otorgados') {
          query = query.eq('status', 'confirmado');
        } else if (activeTab === 'espera') {
          query = query.in('status', ['presente', 'en-llamado', 'en-consultorio', 'atendido']);
        }

        const { data, error: dbError } = await query.order('appointment_time', { ascending: true });
        if (dbError) throw dbError;

        let result = data || [];
        if (activeTab === 'informes') {
          result = result.filter(app => app.study_details?.attached_files?.length > 0);
        }
        
        setReportData(result);

      } else if (['borrados', 'pacientes-borrados', 'caja-borrados', 'informes-borrados'].includes(activeTab)) {
        let query = supabase.from('audit_logs').select('*');
        
        if (activeTab === 'borrados') {
          query = query.eq('action', 'BORRADO TURNO');
        } else if (activeTab === 'pacientes-borrados') {
          query = query.eq('action', 'BORRADO PACIENTE');
        } else if (activeTab === 'caja-borrados') {
          query = query.eq('action', 'BORRADO CAJA');
        } else if (activeTab === 'informes-borrados') {
          query = query.eq('action', 'BORRADO TURNO');
        }

        if (selectedDate) {
          if (activeTab !== 'borrados' && activeTab !== 'informes-borrados') {
            query = query.gte('created_at', selectedDate + 'T00:00:00').lte('created_at', selectedDate + 'T23:59:59');
          }
        }

        const { data, error: dbError } = await query.order('created_at', { ascending: true });
        if (dbError) throw dbError;

        let filteredLogs = data || [];

        if (selectedDate && (activeTab === 'borrados' || activeTab === 'informes-borrados')) {
          filteredLogs = filteredLogs.filter(log => log.details?.appointment_date === selectedDate);
        }
        if (selectedProf && selectedProf !== 'VER TODOS' && (activeTab === 'borrados' || activeTab === 'informes-borrados')) {
          filteredLogs = filteredLogs.filter(log => log.details?.professional_name === selectedProf);
        }

        let result = [];
        if (activeTab === 'borrados') {
          result = filteredLogs.map(log => ({
            id: log.id,
            appointment_date: log.details?.appointment_date || '---',
            appointment_time: log.details?.appointment_time || '00:00:00',
            patient_name: log.details?.patient_name || '---',
            patient_dni: log.details?.patient_dni || '---',
            patient_phone: log.details?.patient_phone || '',
            patient_email: log.details?.patient_email || '',
            study_type: log.details?.study_type || 'Consulta General',
            professional_name: log.details?.professional_name || '---',
            deleted_at: new Date(log.created_at).toLocaleString('es-AR'),
            deleted_by: log.user_email || '---',
            notes: log.details?.admin_notes || '---'
          }));
        } else if (activeTab === 'informes-borrados') {
          result = filteredLogs
            .filter(log => log.details?.study_details?.attached_files?.length > 0)
            .map(log => ({
              id: log.id,
              appointment_date: log.details?.appointment_date || '---',
              appointment_time: log.details?.appointment_time || '00:00:00',
              patient_name: log.details?.patient_name || '---',
              patient_dni: log.details?.patient_dni || '---',
              patient_phone: log.details?.patient_phone || '',
              patient_email: log.details?.patient_email || '',
              study_type: log.details?.study_type || 'Consulta General',
              professional_name: log.details?.professional_name || '---',
              deleted_at: new Date(log.created_at).toLocaleString('es-AR'),
              deleted_by: log.user_email || '---',
              notes: 'Informe adjunto eliminado'
            }));
        } else if (activeTab === 'pacientes-borrados') {
          result = filteredLogs.map(log => ({
            id: log.id,
            patient_name: log.details?.full_name || '---',
            patient_dni: log.details?.dni || '---',
            patient_phone: log.details?.phone || '---',
            patient_email: log.details?.email || '---',
            insurance: log.details?.insurance_provider || '---',
            deleted_at: new Date(log.created_at).toLocaleString('es-AR'),
            deleted_by: log.user_email || '---'
          }));
        } else if (activeTab === 'caja-borrados') {
          result = filteredLogs.map(log => ({
            id: log.id,
            description: log.details?.description || '---',
            amount: log.details?.amount || '0',
            type: log.details?.type || '---',
            category: log.details?.category || '---',
            deleted_at: new Date(log.created_at).toLocaleString('es-AR'),
            deleted_by: log.user_email || '---'
          }));
        }

        setReportData(result);
      }
    } catch (err) {
      console.error('Error fetching reports:', err);
      setError(err.message || 'Error desconocido al cargar datos');
      addNotification('Error al cargar listados', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    Promise.resolve().then(() => {
      fetchConfig();
    });
  }, []);

  useEffect(() => {
    Promise.resolve().then(() => {
      fetchReportData();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedDate, selectedProf]);

  const getPieData = () => {
    const counts = {};
    reportData.forEach(app => {
      counts[app.status] = (counts[app.status] || 0) + 1;
    });
    return Object.keys(counts).map(k => ({ name: k.toUpperCase(), value: counts[k] }));
  };
  
  const getProfData = () => {
    const counts = {};
    reportData.forEach(app => {
      counts[app.professional_name || 'Sin Asignar'] = (counts[app.professional_name || 'Sin Asignar'] || 0) + 1;
    });
    return Object.keys(counts).map(k => ({ name: k, turnos: counts[k] }));
  };

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#ffc658'];

  return (
    <div className="reports-page">
      <div className="page-header">
        <div className="title-section">
          <h1>Listados y Auditoría</h1>
          <p>Consulta y exporta el historial completo de la clínica.</p>
        </div>
        <div className="header-actions">
          <button className="btn-secondary" onClick={() => addNotification('Generando Excel...', 'info')}>
            <Download size={18} /> EXCEL
          </button>
          <button className="btn-primary" onClick={() => window.print()}>
            <Printer size={18} /> IMPRIMIR LISTADO
          </button>
        </div>
      </div>

      <div className="reports-main-container card glass-panel-premium">
        {/* Botonera Superior - Estilo Blipdoc Original */}
        <div className="report-tabs-container">
          <div className="tabs-row">
            {reportButtons.slice(0, 6).map(btn => (
              <button 
                key={btn.id} 
                className={`tab-btn-blip ${activeTab === btn.id ? 'active' : ''}`}
                onClick={() => setActiveTab(btn.id)}
              >
                {btn.label}
              </button>
            ))}
          </div>
          <div className="tabs-row second">
            {reportButtons.slice(6).map(btn => (
              <button 
                key={btn.id} 
                className={`tab-btn-blip ${activeTab === btn.id ? 'active' : ''}`}
                onClick={() => setActiveTab(btn.id)}
              >
                {btn.label}
              </button>
            ))}
          </div>
        </div>

        <div className="reports-content-area">
          <div className="report-header-flex">
            <h2 className="report-current-title">
              {reportButtons.find(b => b.id === activeTab)?.label}
            </h2>
          </div>
          
          <div className="blip-filter-bar">
            <div className="blip-filter-item">
              <label>Fecha de los Turnos</label>
              <div className="blip-input-group">
                <input 
                  type="date" 
                  value={selectedDate} 
                  onChange={(e) => setSelectedDate(e.target.value)} 
                />
                <CalendarIcon size={16} className="blip-icon" />
              </div>
            </div>

            <div className="blip-filter-item">
              <label>Profesional</label>
              <div className="blip-input-group">
                 <select 
                   value={selectedProf} 
                   onChange={(e) => setSelectedProf(e.target.value)}
                   disabled={!isAdmin || activeTab === 'pacientes-borrados' || activeTab === 'caja-borrados'}
                 >
                   {!isAdmin ? (
                     <option value={staffName}>{staffName}</option>
                   ) : (
                     <>
                       <option value="VER TODOS">-- TODOS LOS PROFESIONALES --</option>
                       {professionals.map(p => <option key={p.id} value={p.full_name || p.name}>{p.full_name || p.name}</option>)}
                     </>
                   )}
                 </select>
                <ChevronDown size={16} className="blip-icon" />
              </div>
            </div>
          </div>

          <div className="report-results-container">
            {activeTab === 'graficos' ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', padding: '20px' }}>
                <div className="card glass-panel" style={{ flex: '1 1 400px', minWidth: 0, padding: '20px' }}>
                  <h3 style={{ color: 'white', marginBottom: '20px', textAlign: 'center' }}>Distribución de Estados</h3>
                  {reportData.length === 0 ? <p className="text-center-p">No hay datos</p> : (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie data={getPieData()} cx="50%" cy="50%" outerRadius={100} dataKey="value" label>
                          {getPieData().map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                        </Pie>
                        <Tooltip contentStyle={{ backgroundColor: '#1e1e2d', borderColor: '#333', color: '#fff' }} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </div>
                <div className="card glass-panel" style={{ flex: '1 1 500px', minWidth: 0, padding: '20px' }}>
                  <h3 style={{ color: 'white', marginBottom: '20px', textAlign: 'center' }}>Turnos por Profesional</h3>
                  {reportData.length === 0 ? <p className="text-center-p">No hay datos</p> : (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={getProfData()} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.05)" />
                        <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#aaa' }} />
                        <YAxis tick={{ fontSize: 10, fill: '#aaa' }} />
                        <Tooltip cursor={{ fill: 'rgba(255,255,255,0.02)' }} contentStyle={{ backgroundColor: '#1e1e2d', borderColor: '#333', color: '#fff' }} />
                        <Bar dataKey="turnos" fill="#00E676" radius={[4, 4, 0, 0]} barSize={40} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>
            ) : (
            <table className="blip-report-table">
              <thead>
                {['diario', 'otorgados', 'cancelados', 'espera', 'ausentes', 'informes'].includes(activeTab) && (
                  <tr>
                    <th style={{ width: '110px' }}>Fecha</th>
                    <th style={{ width: '80px' }}>Hora</th>
                    <th style={{ width: '220px' }}>Paciente</th>
                    <th style={{ width: '180px' }}>Profesional</th>
                    <th>Estudio</th>
                    <th style={{ width: '130px' }}>Estado</th>
                    <th style={{ width: '140px' }}>Verificación</th>
                    <th style={{ width: '90px' }}>Acciones</th>
                  </tr>
                )}
                {['borrados', 'informes-borrados'].includes(activeTab) && (
                  <tr>
                    <th style={{ width: '110px' }}>Fecha Turno</th>
                    <th style={{ width: '80px' }}>Hora</th>
                    <th style={{ width: '200px' }}>Paciente</th>
                    <th style={{ width: '180px' }}>Profesional</th>
                    <th>Estudio</th>
                    <th style={{ width: '170px' }}>Fecha Borrado</th>
                    <th style={{ width: '180px' }}>Borrado Por</th>
                    <th>Notas / Motivo</th>
                  </tr>
                )}
                {activeTab === 'pacientes-borrados' && (
                  <tr>
                    <th style={{ width: '200px' }}>Paciente</th>
                    <th style={{ width: '120px' }}>DNI</th>
                    <th style={{ width: '140px' }}>Celular</th>
                    <th style={{ width: '200px' }}>Email</th>
                    <th>Obra Social</th>
                    <th style={{ width: '170px' }}>Fecha Borrado</th>
                    <th>Borrado Por</th>
                  </tr>
                )}
                {activeTab === 'caja-borrados' && (
                  <tr>
                    <th>Descripción</th>
                    <th style={{ width: '120px' }}>Monto</th>
                    <th style={{ width: '120px' }}>Tipo</th>
                    <th style={{ width: '150px' }}>Categoría</th>
                    <th style={{ width: '170px' }}>Fecha Borrado</th>
                    <th>Borrado Por</th>
                  </tr>
                )}
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={
                      ['diario', 'otorgados', 'cancelados', 'espera', 'ausentes', 'informes'].includes(activeTab) ? 8 :
                      ['borrados', 'informes-borrados'].includes(activeTab) ? 8 :
                      activeTab === 'pacientes-borrados' ? 7 : 6
                    } className="text-center-p">
                      Cargando registros...
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan={
                      ['diario', 'otorgados', 'cancelados', 'espera', 'ausentes', 'informes'].includes(activeTab) ? 8 :
                      ['borrados', 'informes-borrados'].includes(activeTab) ? 8 :
                      activeTab === 'pacientes-borrados' ? 7 : 6
                    } className="error-msg-table">
                      ERROR: {error}
                    </td>
                  </tr>
                ) : reportData.length === 0 ? (
                  <tr>
                    <td colSpan={
                      ['diario', 'otorgados', 'cancelados', 'espera', 'ausentes', 'informes'].includes(activeTab) ? 8 :
                      ['borrados', 'informes-borrados'].includes(activeTab) ? 8 :
                      activeTab === 'pacientes-borrados' ? 7 : 6
                    } className="no-data-msg">
                      No se encontraron registros para mostrar con los filtros seleccionados.
                    </td>
                  </tr>
                ) : (
                  reportData.map(row => {
                    if (['diario', 'otorgados', 'cancelados', 'espera', 'ausentes', 'informes'].includes(activeTab)) {
                      return (
                        <tr key={row.id}>
                          <td>{row.appointment_date}</td>
                          <td className="time-col">{row.appointment_time?.substring(0, 5)} hs</td>
                          <td>
                            <div className="patient-report-cell">
                              <span className="p-name-bold">{row.patient_name}</span>
                              <span className="p-dni-small">DNI: {row.patient_dni || '---'}</span>
                            </div>
                          </td>
                          <td>
                            <span className="p-name-bold">{row.professional_name || 'Sin Profesional'}</span>
                          </td>
                          <td>
                            <div className="study-type-cell">
                              <span className="study-type-text">{row.study_type || 'Consulta General'}</span>
                              {row.study_details?.insurance && <span className="os-badge-small">{row.study_details.insurance}</span>}
                            </div>
                          </td>
                          <td>
                            <StatusDropdown 
                              value={row.status || 'pendiente'} 
                              onChange={() => fetchReportData()} 
                            />
                          </td>
                          <td>
                            {(!row.verification_status || row.verification_status === 'not_verified') && (
                              <button 
                                className="btn-verify-badge" 
                                onClick={() => {
                                  setSelectedAppt(row);
                                  setIsVerifyModalOpen(true);
                                }}
                              >
                                <Mail size={12} /> Solicitar
                              </button>
                            )}
                            {row.verification_status === 'sent' && (
                              <span className="v-status sent">
                                <Mail size={12} /> Enviado
                              </span>
                            )}
                            {row.verification_status === 'confirmed' && (
                              <span className="v-status confirmed">
                                <CheckCircle2 size={12} /> Confirmado
                              </span>
                            )}
                            {row.verification_status === 'rejected' && (
                              <span className="v-status rejected" title={row.verification_notes}>
                                <AlertCircle size={12} /> Falta Info
                              </span>
                            )}
                          </td>
                          <td>
                            <button 
                              className="btn-icon-table" 
                              title="Descargar PDF" 
                              onClick={() => addNotification('Descargando legajo...', 'info')}
                            >
                              <Download size={14} />
                            </button>
                          </td>
                        </tr>
                      );
                    } else if (['borrados', 'informes-borrados'].includes(activeTab)) {
                      return (
                        <tr key={row.id}>
                          <td>{row.appointment_date}</td>
                          <td className="time-col">{row.appointment_time?.substring(0, 5)} hs</td>
                          <td>
                            <div className="patient-report-cell">
                              <span className="p-name-bold">{row.patient_name}</span>
                              <span className="p-dni-small">DNI: {row.patient_dni || '---'}</span>
                            </div>
                          </td>
                          <td>
                            <span className="p-name-bold">{row.professional_name || 'Sin Profesional'}</span>
                          </td>
                          <td>
                            <span className="study-type-text">{row.study_type || 'Consulta General'}</span>
                          </td>
                          <td>{row.deleted_at}</td>
                          <td>{row.deleted_by}</td>
                          <td>
                            <span className="obs-text">{row.notes}</span>
                          </td>
                        </tr>
                      );
                    } else if (activeTab === 'pacientes-borrados') {
                      return (
                        <tr key={row.id}>
                          <td className="p-name-bold">{row.patient_name}</td>
                          <td>{row.patient_dni}</td>
                          <td>{row.patient_phone}</td>
                          <td>{row.patient_email}</td>
                          <td>{row.insurance}</td>
                          <td>{row.deleted_at}</td>
                          <td>{row.deleted_by}</td>
                        </tr>
                      );
                    } else if (activeTab === 'caja-borrados') {
                      return (
                        <tr key={row.id}>
                          <td className="p-name-bold">{row.description}</td>
                          <td className="time-col">${row.amount}</td>
                          <td style={{ textTransform: 'uppercase' }}>{row.type}</td>
                          <td>{row.category}</td>
                          <td>{row.deleted_at}</td>
                          <td>{row.deleted_by}</td>
                        </tr>
                      );
                    }
                    return null;
                  })
                )}
              </tbody>
            </table>
            )}
          </div>
        </div>
      </div>

      <VerificationModal 
        isOpen={isVerifyModalOpen} 
        onClose={() => {
          setIsVerifyModalOpen(false);
          fetchReportData();
        }} 
        appointment={selectedAppt} 
      />
    </div>
  );
};

export default Reports;
